// Imports
import React, { useState, useEffect } from 'react';
import Button from '../../../../../shareables/foundation/front-end/components/buttons/standard';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ButtonGroup from '../../../../../shareables/foundation/front-end/components/buttons/group';
import theme from '../../../../../shareables/theme';
import styled from 'styled-components/macro';


// Styled component
const PageNavigationButton = styled(Button)`
	@media (max-width: ${theme.breakpoints.mobile}px) {
		padding: 0 0.25em;
	}
`;


// Define the accepted props
interface Props {
	currentPageIndex: number;
	pageCount: number;
	goToPage: (updater: number) => void;
	addBorders?: boolean;
}


// Function component
const PageNavigation: React.FC<Props> = ({ currentPageIndex, pageCount, goToPage, addBorders }) => {
	// Use state
	const [isMobile, setIsMobile] = useState<boolean>(
		window.matchMedia(`(max-width: ${theme.breakpoints.mobile}px)`).matches
	);
	
	
	// Listener for media change
	useEffect(() => {
		// Handle media changes
		function handleMediaChanges(this: MediaQueryList): void {
			setIsMobile(this.matches);
		}
		
		
		// Check for media query match
		const mediaMatch = window.matchMedia(`(max-width: ${theme.breakpoints.mobile}px)`);
		
		
		// Handle older browsers not extending from EventTarget
		if (!(mediaMatch instanceof EventTarget)) {
			// @ts-expect-error: Typescript expects `MediaQueryList` to always extend EventTarget, we are testing for when it does not
			mediaMatch.addListener(handleMediaChanges); // eslint-disable-line @typescript-eslint/no-unsafe-call
			
			return () => {
				// @ts-expect-error: Typescript expects `MediaQueryList` to always extend EventTarget, we are testing for when it does not
				mediaMatch.removeListener(handleMediaChanges); // eslint-disable-line @typescript-eslint/no-unsafe-call
			};
		}
		
		
		// Listen for changes to the media
		mediaMatch.addEventListener('change', handleMediaChanges);
		
		
		// Return
		return () => {
			mediaMatch.removeEventListener('change', handleMediaChanges);
		};
	}, []);
	
	
	// Configure the desired number of items (excludes left/right arrow buttons)
	let maxItems = 10;
	
	if (isMobile) {
		maxItems = 4;
	}
	
	
	// Determine values (start and end are both inclusive)
	const countBeforeIndex = Math.floor(maxItems / 3);
	const numberAfterEllipsis = Math.floor(maxItems / 4);
	
	const pageNumbersJSX: JSX.Element[] = [];
	let startIndex = currentPageIndex - countBeforeIndex;
	
	if (startIndex < 0) {
		startIndex = 0;
	}
	
	let endIndex = startIndex + maxItems - 1;
	
	if (endIndex >= pageCount) {
		endIndex = pageCount - 1;
		startIndex = endIndex - maxItems + 1;
	}
	
	if (startIndex < 0) {
		startIndex = 0;
	}
	
	
	// Create initial set of page numbers
	for (let i = startIndex; i <= endIndex; i++) {
		pageNumbersJSX.push(
			<PageNavigationButton
				key={i}
				type='button'
				color={currentPageIndex === i ? 'primary' : 'plain'}
				border={addBorders}
				onClick={() => goToPage(i)}
				aria-label={`Page ${i + 1}`}
			>
				{i + 1}
			</PageNavigationButton>
		);
	}
	
	
	// Replace the final numbers if there's more available than what's displayed
	if (endIndex < pageCount - 1) {
		for (let i = 0; i < numberAfterEllipsis + 1; i++) {
			const pageNumber = pageCount - i;
			
			if (i === numberAfterEllipsis) {
				pageNumbersJSX[pageNumbersJSX.length - i - 1] = (
					<span key={pageNumber - 1} style={{ alignSelf: 'center' }}>
						…
					</span>
				);
				
				continue;
			}
			
			pageNumbersJSX[pageNumbersJSX.length - i - 1] = (
				<PageNavigationButton
					key={pageNumber - 1}
					type='button'
					color={currentPageIndex === pageNumber - 1 ? 'primary' : 'plain'}
					border={addBorders}
					onClick={() => goToPage(pageNumber - 1)}
					aria-label={`Page ${pageNumber}`}
				>
					{pageNumber}
				</PageNavigationButton>
			);
		}
	}
	
	
	// Return JSX
	return (
		<ButtonGroup role='navigation' disableWrapping>
			<PageNavigationButton
				type='button'
				color='plain'
				border={addBorders}
				aria-label='Go to previous page'
				disabled={currentPageIndex === 0}
				onClick={currentPageIndex === 0 ? undefined : () => goToPage(currentPageIndex - 1)}
			>
				<FontAwesomeIcon icon={faAngleLeft} />
			</PageNavigationButton>
			
			{pageNumbersJSX}
			
			<PageNavigationButton
				type='button'
				color='plain'
				border={addBorders}
				aria-label='Go to next page'
				onClick={currentPageIndex === pageCount - 1 ? undefined : () => goToPage(currentPageIndex + 1)}
				disabled={currentPageIndex === pageCount - 1}
			>
				<FontAwesomeIcon icon={faAngleRight} />
			</PageNavigationButton>
		</ButtonGroup>
	);
};

export default PageNavigation;
